import type {
    ChangePasswordRequest,
    DataResponse,
    ForgotPasswordRequest,
    LegalInfo,
    RegisterAccountRequest,
    ResetPasswordRequest,
} from '@/types';
import type { AxiosInstance, AxiosResponse } from 'axios';

class AuthService {
    private readonly apiService;

    constructor(apiService: AxiosInstance) {
        this.apiService = apiService;
    }

    async csrf(): Promise<AxiosResponse> {
        return this.apiService.get('/sanctum/csrf-cookie');
    }

    async forgotPassword(request: ForgotPasswordRequest): Promise<AxiosResponse> {
        await this.csrf();
        return this.apiService.post('/forgot-password', request);
    }

    async resetPassword(request: ResetPasswordRequest): Promise<AxiosResponse> {
        await this.csrf();
        return this.apiService.post('/reset-password', request);
    }

    async changePassword(request: ChangePasswordRequest): Promise<AxiosResponse> {
        await this.csrf();
        return this.apiService.post('/change-password', request);
    }

    /** Public: read before the sign-up form renders, so no CSRF round-trip. */
    legal(): Promise<AxiosResponse<DataResponse<LegalInfo>>> {
        return this.apiService.get('/api/legal');
    }

    async register(request: RegisterAccountRequest): Promise<AxiosResponse> {
        await this.csrf();
        return this.apiService.post('/register', request);
    }

    async sendVerificationEmail(): Promise<AxiosResponse> {
        await this.csrf();
        return this.apiService.post('/email/verification-notification');
    }

    async verifyEmail(id: string, hash: string, expires: number, signature: string): Promise<AxiosResponse> {
        await this.csrf();
        return this.apiService.get(`/email/verify/${id}/${hash}?expires=${expires}&signature=${signature}`);
    }

    async verifyTwoFactor(code: string, rememberDevice = false): Promise<AxiosResponse> {
        await this.csrf();
        return this.apiService.post('/login/2fa', { code, remember_device: rememberDevice });
    }
}

export default AuthService;
